import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, FileText, X, ShieldCheck, Lock, CheckCircle2, AlertCircle, Loader2, FileSignature, Image as ImageIcon, FileSpreadsheet, Folder, FileCheck2, Stamp, FileLock2, ScanLine, KeyRound, Eye, EyeOff } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useIsB2B } from "@/hooks/use-b2b";
import { toast } from "sonner";
import { showSuccessNotification } from "@/components/site/SuccessNotificationPopup";
import { notifyNewOrder } from "@/lib/notifications.functions";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { useWatermarkEnabled } from "@/hooks/use-watermark-settings";
import { fetchPackageB2BPriceMap } from "@/lib/content";
const UploadWarningPopup = lazy(() => import("@/components/site/UploadWarningPopup").then((m) => ({ default: m.UploadWarningPopup })));

function effectivePrice(pkg: any, isB2B: boolean): string | number | null | undefined {
  if (isB2B && pkg?.b2b_price != null && String(pkg.b2b_price).trim() !== "") return pkg.b2b_price;
  return pkg?.price;
}

type PortalCfg = { title: string; idLabel: string; idPlaceholder: string; pwPlaceholder: string; hint: string; storedLabel: string };

function getPortalConfig(serviceSlug?: string | null, serviceTitle?: string | null): PortalCfg {
  const s = `${serviceSlug ?? ""} ${serviceTitle ?? ""}`.toLowerCase();
  if (/(income\s*tax\s*return|company\s*tax|আয়কর|রিটার্ন)/.test(s) && !/vat|ভ্যাট|bin/.test(s)) {
    return {
      title: "পূর্ববর্তী বছরের ই-রিটার্ন লগইন তথ্য (ঐচ্ছিক)",
      idLabel: "e-Return User ID / TIN",
      idPlaceholder: "Enter e-Return User ID / TIN",
      pwPlaceholder: "e-Return Password (optional)",
      hint: "পূর্বে ই-রিটার্ন সাবমিট করা থাকলে লগইন তথ্য দিন — দ্রুত ফাইলিং সম্ভব হবে।",
      storedLabel: "e-Return Portal",
    };
  }
  if (/(vat|bin|ভ্যাট)/.test(s)) {
    return {
      title: "ভ্যাট পোর্টাল / BIN অ্যাকাউন্টের তথ্য (ঐচ্ছিক)",
      idLabel: "VAT User ID / BIN",
      idPlaceholder: "Enter VAT User ID / BIN",
      pwPlaceholder: "VAT Portal Password (optional)",
      hint: "বিদ্যমান BIN/VAT অ্যাকাউন্ট থাকলে লগইন তথ্য দিন।",
      storedLabel: "VAT / BIN Portal",
    };
  }
  if (/(e[-\s]?tin|tin[-\s]?registration|ই[-\s]?টিন)/.test(s)) {
    return {
      title: "বিদ্যমান ই-টিন পোর্টাল লগইন তথ্য (ঐচ্ছিক)",
      idLabel: "TIN or NID",
      idPlaceholder: "Enter TIN or NID",
      pwPlaceholder: "e-TIN Portal Password (optional)",
      hint: "পূর্বের ই-টিন রিকভারি/আপডেটের জন্য লগইন তথ্য দিন।",
      storedLabel: "e-TIN Portal",
    };
  }
  const name = (serviceTitle && serviceTitle.trim()) || "সার্ভিস";
  return {
    title: `${name} লগইন তথ্য (ঐচ্ছিক)`,
    idLabel: "Associated User ID / License No.",
    idPlaceholder: "Enter Associated User ID / License No.",
    pwPlaceholder: "Password (optional)",
    hint: "এই সার্ভিসের সাথে সম্পর্কিত কোনো পোর্টাল অ্যাকাউন্ট থাকলে তথ্য দিন।",
    storedLabel: `${name} Portal`,
  };
}

export const Route = createFileRoute("/upload")({
  validateSearch: (s: Record<string, unknown>) => {
    const out: { service?: string; package?: string; packageId?: string } = {};
    const svc = typeof s.service === "string" ? s.service.trim() : "";
    const pkg = typeof s.package === "string" ? s.package.trim() : "";
    const pid = typeof s.packageId === "string" ? s.packageId.trim() : "";
    if (svc) out.service = svc;
    if (pkg) out.package = pkg;
    if (pid) out.packageId = pid;
    return out;
  },
  head: () => ({
    meta: [
      { title: "Upload Documents — Online Tax VAT Bangladesh" },
      { name: "description", content: "Securely upload your tax documents to start your service request." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [
      { rel: "canonical", href: "https://onlinetaxvat.com/upload" },
    ],
  }),
  component: UploadPage,
});

type Item = { id: string; file: File; progress: number; status: "uploading" | "done" | "error"; url?: string; error?: string };

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
const MAX_MB = 10;

function UploadPage() {
  const { t } = useTranslation();
  const showWatermark = useWatermarkEnabled("upload");
  const { user, loading: authLoading } = useAuth();
  const { isB2B, isVIP } = useIsB2B();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [items, setItems] = useState<Item[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [pkg, setPkg] = useState<any>(null);
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [phone, setPhone] = useState(""); const [notes, setNotes] = useState("");
  const [portalUserId, setPortalUserId] = useState("");
  const [portalPassword, setPortalPassword] = useState("");
  const [showPortalPw, setShowPortalPw] = useState(false);
  const [b2bTaxInput, setB2bTaxInput] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [b2bLocked, setB2bLocked] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const b2bTaxAmount = Math.max(0, Number(b2bTaxInput || 0));
  const b2bGovtChargeFee = Math.round(b2bTaxAmount * 0.01 * 100) / 100;
  const b2bTaxTotal = b2bTaxAmount + b2bGovtChargeFee;
  const b2bHasTax = b2bTaxAmount > 0;

  const portalCfg = getPortalConfig(pkg?.services?.slug ?? search.service, pkg?.services?.title);

  useEffect(() => {
    const pid = search.packageId;
    if (!pid) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("packages").select("*").eq("id", pid).maybeSingle();
      if (data) {
        const b2bPrice = (await fetchPackageB2BPriceMap("packages", [data.id])).get(data.id) ?? null;
        if (cancelled) return;
        let svc: any = null;
        if (data.service_code) {
          const { data: s } = await supabase.from("services").select("slug,title").eq("service_code", data.service_code).maybeSingle();
          svc = s;
        }
        if (!svc && data.service_id) {
          const { data: s } = await supabase.from("services").select("slug,title").eq("id", data.service_id).maybeSingle();
          svc = s;
        }
        setPkg({ ...data, b2b_price: b2bPrice, services: svc });
        return;
      }
      // Fallback: visa tax package
      const { data: visa } = await supabase.from("visa_tax_packages").select("*").eq("id", pid).maybeSingle();
      if (visa) {
        const b2bPrice = (await fetchPackageB2BPriceMap("visa_tax_packages", [visa.id])).get(visa.id) ?? null;
        if (cancelled) return;
        setPkg({
          ...visa,
          b2b_price: b2bPrice,
          slug: `visa-tax-${visa.duration_years}y`,
          service_slug: "visa-tax",
          services: { slug: "visa-tax", title: "Visa Tax File" },
        });
      } else {
        if (cancelled) return;
        setPkg(null);
      }
    })();
    return () => { cancelled = true; };
  }, [search.packageId]);

  useEffect(() => {
    if (user) {
      supabase.from("profiles").select("*").eq("id", user.id).maybeSingle().then(({ data }) => {
        if (data) { setName(data.full_name ?? ""); setEmail(data.email ?? user.email ?? ""); setPhone(data.phone ?? ""); }
        else setEmail(user.email ?? "");
      });
    }
  }, [user]);

  // B2B/VIP pending-invoice locker: pre-flight check so the form is hidden
  // entirely when the user owes money on a previously delivered order.
  useEffect(() => {
    if (!user || !isB2B) { setB2bLocked(false); return; }
    let cancelled = false;
    supabase.rpc("b2b_has_pending_balance" as any, { _user_id: user.id })
      .then(({ data }) => { if (!cancelled) setB2bLocked(data === true); });
    return () => { cancelled = true; };
  }, [user, isB2B]);

  async function uploadOne(file: File): Promise<{ url: string } | { error: string }> {
    if (!user) return { error: "Not signed in" };
    if (file.size > MAX_MB * 1024 * 1024) return { error: t("uploadPage.errFileSize", { max: MAX_MB }) };
    const path = `${user.id}/${Date.now()}-${file.name.replace(/[^\w.\-]/g, "_")}`;
    const { error } = await supabase.storage.from("documents").upload(path, file);
    if (error) return { error: error.message };
    const { data } = await supabase.storage.from("documents").createSignedUrl(path, 60 * 60 * 24 * 30);
    return { url: data?.signedUrl ?? path };
  }

  async function handleFiles(files: FileList | File[]) {
    if (!user) { toast.error(t("uploadPage.errSignIn")); navigate({ to: "/login" }); return; }
    const arr = Array.from(files);
    for (const file of arr) {
      const id = crypto.randomUUID();
      setItems((p) => [...p, { id, file, progress: 30, status: "uploading" }]);
      const res = await uploadOne(file);
      setItems((p) => p.map((x) => x.id === id ? ("error" in res
        ? { ...x, status: "error", error: res.error, progress: 0 }
        : { ...x, status: "done", progress: 100, url: res.url }) : x));
    }
  }

  const done = items.filter((i) => i.status === "done");
  const isCustomOrder = !pkg;
  const canSubmit = !!user && done.length > 0 && !!name && !!phone && !submitting && (!search.packageId || !!pkg);

  async function submitOrder() {
    if (!canSubmit || !user) return;
    setSubmitting(true);
    // B2B/VIP pending-invoice locker: block ANY new order while a previously
    // delivered order still has an outstanding package balance. Enforced
    // server-side by trigger trg_block_b2b_order_when_dues; this is the UX
    // pre-check so the user sees a friendly message instead of a raw error.
    if (isB2B) {
      const { data: hasDue } = await supabase
        .rpc("b2b_has_pending_balance" as any, { _user_id: user.id });
      if (hasDue === true) {
        setSubmitting(false);
        toast.error(
          "আপনার পূর্ববর্তী অর্ডারের পেমেন্ট বকেয়া রয়েছে। অনুগ্রহ করে আগের ইনভয়েসটি পরিশোধ করে নতুন অর্ডার সাবমিট করুন।",
          { duration: 9000 }
        );
        return;
      }
    }
    const refId = "OTV-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    const rawPrice = pkg ? effectivePrice(pkg, isB2B) : 0;
    const priceN = Number(rawPrice);
    const pkgPrice = Number.isFinite(priceN) && String(rawPrice ?? "").trim() !== "" ? priceN : 0;
    // B2B tax-only flow: when B2B user enters a tax amount > 0, force upfront payment of that tax + 1% govt fee
    const b2bTaxFlow = isB2B && b2bHasTax;
    const totalPrice = b2bTaxFlow ? (pkgPrice + b2bTaxTotal) : pkgPrice;
    const insertPayload: any = {
      user_id: user.id,
      reference_id: refId,
      service_slug: pkg?.services?.slug ?? search.service ?? "custom",
      service_title: pkg?.services?.title ?? "Custom / Manual Service",
      package_slug: pkg?.slug ?? "custom",
      package_name: pkg?.name ?? "Custom Order",
      total_price: totalPrice,
      amount: (isB2B && !b2bTaxFlow) || isCustomOrder ? 0 : null,
      payment_status: isCustomOrder
        ? "pending_quote"
        : b2bTaxFlow
          ? "pending_verification"
          : (isB2B ? "postpaid_due" : "pending_verification"),
      notes,
      document_urls: done.map((d) => d.url!),
      customer_name: name, customer_email: email, customer_phone: phone,
      portal_user_id: portalUserId.trim() || null,
      portal_password: portalPassword.trim() || null,
      portal_label: (portalUserId.trim() || portalPassword.trim()) ? portalCfg.storedLabel : null,
    };
    if (b2bTaxFlow) {
      insertPayload.tax_amount = b2bTaxAmount;
      insertPayload.govt_charge_fee = b2bGovtChargeFee;
    }
    const { data, error } = await supabase.from("orders").insert(insertPayload).select().single();
    setSubmitting(false);
    if (error) return toast.error(error.message);
    notifyNewOrder({ data: { customerName: name } }).catch(() => {});
    if (isB2B) { import("@/components/site/B2BCommissionPopup").then((m) => m.triggerB2BCommissionPopup()).catch(() => {}); }
    if (isCustomOrder) {
      toast.success("কাস্টম অর্ডার সফলভাবে সাবমিট হয়েছে — আমরা শীঘ্রই যোগাযোগ করব।");
      showSuccessNotification({
        title: "✅ কাস্টম অর্ডার সাবমিট সফল",
        message: `Ref: ${refId} — আমাদের টিম শীঘ্রই আপনার সাথে যোগাযোগ করবে।`,
      });
      navigate({ to: "/dashboard" });
    } else if (b2bTaxFlow) {
      toast.success(`ট্যাক্স পেমেন্ট প্রয়োজন — ৳${b2bTaxTotal.toLocaleString()} পরিশোধ করে অর্ডার সম্পন্ন করুন।`);
      showSuccessNotification({
        title: "💳 ট্যাক্স পেমেন্ট প্রয়োজন",
        message: `Ref: ${refId} — অর্ডার ফাইনাল করতে ট্যাক্স পরিশোধ করুন।`,
      });
      navigate({ to: "/payment", search: { order: data.id } as any });
    } else if (isB2B) {
      toast.success("B2B অর্ডার তৈরি হয়েছে (পোস্ট-পেইড) — পরে পেমেন্ট করতে পারবেন।");
      showSuccessNotification({
        title: "✅ ফর্ম সফলভাবে সাবমিট হয়েছে",
        message: `Ref: ${refId} — আপনার রিকোয়েস্ট গ্রহণ করা হয়েছে।`,
      });
      navigate({ to: "/dashboard" });
    } else {
      toast.success(t("uploadPage.toastCreated"));
      showSuccessNotification({
        title: "✅ ট্যাক্স ফর্ম সাবমিট সফল",
        message: `Ref: ${refId} — এখন পেমেন্ট সম্পন্ন করুন।`,
      });
      navigate({ to: "/payment", search: { order: data.id } as any });
    }
  }

  if (authLoading || !user) {
    return <SiteShell><div className="container-page py-24 max-w-md text-center" style={{ contain: "layout style" }}>
      <h1 className="font-display text-2xl font-bold home-hero-title">{t("uploadPage.signinTitle")}</h1>
      <p className="text-muted-foreground mt-2">{t("uploadPage.signinDesc")}</p>
      <div className="mt-6 flex gap-2 justify-center" style={{ minHeight: 44 }}>
        {authLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
        ) : (
          <>
            <Button asChild className="gradient-primary text-primary-foreground"><Link to="/login">{t("uploadPage.signin")}</Link></Button>
            <Button asChild variant="outline"><Link to="/signup">{t("uploadPage.signup")}</Link></Button>
          </>
        )}
      </div>
    </div></SiteShell>;
  }


  if (b2bLocked) {
    return <SiteShell><div className="container-page py-24 max-w-2xl">
      <div className="rounded-2xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-rose-50 p-8 text-center shadow-lg">
        <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-600">
          <FileLock2 className="h-8 w-8" />
        </div>
        <h1 className="font-display text-2xl font-bold text-red-800">পেমেন্ট বকেয়া — নতুন অর্ডার ব্লকড</h1>
        <p className="mt-4 text-base leading-relaxed text-red-700 font-semibold">
          আপনার পূর্ববর্তী অর্ডারের পেমেন্ট বকেয়া রয়েছে। অনুগ্রহ করে আগের ইনভয়েসটি পরিশোধ করে নতুন অর্ডার সাবমিট করুন।
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Button asChild className="gradient-primary text-primary-foreground">
            <Link to="/dashboard">ড্যাশবোর্ডে যান — বকেয়া পরিশোধ করুন</Link>
          </Button>
        </div>
      </div>
    </div></SiteShell>;
  }

  return (
    <SiteShell>
      <Suspense fallback={null}><UploadWarningPopup /></Suspense>
      <section className="relative overflow-hidden pt-14 md:pt-20 pb-20">
        {showWatermark && (
          <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            {[
              { Icon: UploadCloud, cls: "top-[6%] left-[4%] text-sky-400/25", size: 86, dur: 8, delay: 0, rot: -8 },
              { Icon: FileText, cls: "top-[10%] right-[6%] text-emerald-400/25", size: 72, dur: 7, delay: 0.4, rot: 6 },
              { Icon: FileSignature, cls: "top-[28%] left-[10%] text-violet-400/25", size: 68, dur: 6.8, delay: 0.8, rot: -4 },
              { Icon: ImageIcon, cls: "top-[34%] right-[12%] text-rose-400/25", size: 70, dur: 7.4, delay: 1.0, rot: 4 },
              { Icon: FileSpreadsheet, cls: "top-[52%] left-[6%] text-teal-400/25", size: 74, dur: 7.6, delay: 0.3, rot: 5 },
              { Icon: Folder, cls: "top-[58%] right-[8%] text-amber-400/25", size: 80, dur: 8.2, delay: 0.7, rot: -6 },
              { Icon: FileCheck2, cls: "bottom-[18%] left-[12%] text-emerald-500/25", size: 70, dur: 7.2, delay: 1.2, rot: 3 },
              { Icon: Stamp, cls: "bottom-[12%] right-[14%] text-indigo-400/25", size: 72, dur: 7.8, delay: 0.5, rot: -5 },
              { Icon: FileLock2, cls: "bottom-[6%] left-[40%] text-cyan-400/25", size: 66, dur: 6.6, delay: 0.9, rot: 4 },
              { Icon: ScanLine, cls: "top-[44%] left-[46%] text-fuchsia-400/20", size: 90, dur: 9, delay: 0.2, rot: 0 },
            ].map(({ Icon, cls, size, dur, delay, rot }, i) => (
              <motion.div
                key={i}
                className={`absolute ${cls} drop-shadow-[0_4px_20px_rgba(0,0,0,0.08)]`}
                initial={{ y: 0, rotate: 0 }}
                animate={{ y: [0, -14, 0], rotate: [0, rot, -rot / 2, 0] }}
                transition={{ duration: dur, delay, repeat: Infinity, ease: "easeInOut" }}
              >
                <Icon style={{ width: size, height: size }} strokeWidth={1.2} />
              </motion.div>
            ))}
          </div>
        )}
        <div className="container-page relative z-10">
          <h1 className="sr-only">Upload Tax & VAT Documents Securely — Online Tax VAT Bangladesh</h1>
          <SectionHeading
            eyebrow={t("uploadPage.eyebrowStep")}
            title={<>{t("uploadPage.titleA")}<span className="gradient-text">{t("uploadPage.titleB")}</span></>}
            description={t("uploadPage.desc")}
          />

          {!pkg && search.packageId && <div className="mt-6 text-center text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> {t("uploadPage.loadingPackage")}</div>}
          {!search.packageId && !pkg && (
            <div className="mt-6 rounded-2xl px-3 py-5 text-center bg-white border border-neutral-200/80 shadow-sm dark:bg-[#06241b] dark:border-emerald-400/40 dark:shadow-[0_0_0_1px_rgba(16,185,129,0.15)] font-sans antialiased">
              <p className="text-2xl md:text-3xl font-extrabold text-neutral-950 dark:text-white tracking-tight">কাস্টম / ম্যানুয়াল অর্ডার মোড</p>
              <p className="mt-2 text-lg md:text-xl font-semibold leading-snug text-neutral-950 dark:text-white">কোনো প্যাকেজ নির্বাচন না করেই ডকুমেন্ট আপলোড করে অর্ডার সাবমিট করতে পারবেন। আমাদের টিম রিভিউ করে কোট পাঠাবে। অথবা প্যাকেজ থেকে নির্দিষ্ট সার্ভিস বেছে নিতে পারেন।</p>
              <Button asChild variant="outline" className="mt-3 text-base font-semibold"><Link to="/services">{t("uploadPage.browseServices")}</Link></Button>
            </div>
          )}
          {pkg && (
            <div className="mt-6 glass-strong rounded-2xl p-5 flex flex-wrap justify-between items-center gap-3">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{pkg.services?.title}</p>
                <p className="font-display font-bold text-lg break-words">{pkg.name}</p>
              </div>
              <div className="text-right flex-shrink-0 min-w-[180px]">
                {isB2B && pkg.b2b_price != null && String(pkg.b2b_price).trim() !== "" && (
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">B2B Price</p>
                )}
                <p className="price-display price-display--sm gradient-text justify-end">{(() => { const raw = effectivePrice(pkg, isB2B); const n = Number(raw); return Number.isFinite(n) && String(raw).trim() !== "" ? `৳ ${n.toLocaleString()}` : String(raw ?? ""); })()}</p>
                {isB2B && pkg.b2b_price != null && String(pkg.b2b_price).trim() !== "" && Number(pkg.price) > Number(pkg.b2b_price) && (
                  <p className="text-xs text-muted-foreground line-through">৳ {Number(pkg.price).toLocaleString()}</p>
                )}
              </div>
            </div>
          )}

          <div className="mt-8 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files); }}
                className={`rounded-3xl border-2 border-dashed px-3 py-10 text-center transition-all bg-white shadow-sm dark:bg-[#06241b] font-sans antialiased ${dragOver ? "border-primary bg-primary/5 scale-[1.01]" : "border-neutral-300 dark:border-emerald-400/40"}`}
              >
                <div className="mx-auto h-16 w-16 rounded-2xl gradient-primary text-primary-foreground flex items-center justify-center shadow-glow">
                  <UploadCloud className="h-8 w-8" />
                </div>
               <h3 className="mt-5 font-display font-extrabold text-2xl md:text-3xl tracking-tight text-neutral-950 dark:text-white">{t("uploadPage.dropTitle")}</h3>
               <p className="mt-2 text-lg md:text-xl font-semibold leading-snug text-neutral-950 dark:text-white">{t("uploadPage.dropDesc", { max: MAX_MB })}</p>
                <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => { if (e.target.files?.length) handleFiles(e.target.files); if (inputRef.current) inputRef.current.value = ""; }} />
                <Button onClick={() => inputRef.current?.click()} className="mt-6 gradient-primary text-primary-foreground shadow-glow text-base font-semibold">{t("uploadPage.chooseFiles")}</Button>
                <div className="mt-5 flex items-center justify-center gap-2 text-sm font-medium text-neutral-700 dark:text-white/90">
                  <Lock className="h-4 w-4" /> {t("uploadPage.storedPrivately")}
                </div>
              </motion.div>

              {items.length > 0 && (
                <div className="glass rounded-2xl p-4 shadow-card">
                  <h4 className="font-semibold text-sm mb-3">{t("uploadPage.filesCount", { count: items.length })}</h4>
                  <ul className="space-y-2">
                    <AnimatePresence>
                      {items.map((item) => (
                        <motion.li key={item.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }}
                          className="rounded-xl bg-background/60 border p-3 flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${item.status === "error" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                            {item.status === "error" ? <AlertCircle className="h-5 w-5" /> : item.status === "done" ? <CheckCircle2 className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.file.name}</p>
                            {item.status === "error" ? <p className="text-xs text-destructive">{item.error}</p>
                              : item.status === "uploading" ? <p className="text-xs text-muted-foreground">{t("uploadPage.uploading")}</p>
                              : <p className="text-xs text-success">{t("uploadPage.uploaded")}</p>}
                          </div>
                          <button onClick={() => setItems((p) => p.filter((x) => x.id !== item.id))} className="h-8 w-8 rounded-lg hover:bg-destructive/10 hover:text-destructive flex items-center justify-center"><X className="h-4 w-4" /></button>
                        </motion.li>
                      ))}
                    </AnimatePresence>
                  </ul>
                </div>
              )}

              {/* Optional portal credentials — dynamic per service */}
              <motion.div
                key={portalCfg.title}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="rounded-2xl p-5 font-sans antialiased bg-white border border-neutral-200 shadow-sm dark:bg-[#06241b] dark:border-emerald-400/40"
              >
                <div className="flex items-start gap-2.5">
                  <div className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 flex items-center justify-center shrink-0">
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-bold text-base leading-snug text-neutral-950 dark:text-white">
                      {portalCfg.title}
                    </h4>
                    <p className="mt-1 text-sm font-medium text-neutral-800 dark:text-white/90">{portalCfg.hint}</p>
                  </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-semibold text-neutral-950 dark:text-white">{portalCfg.idLabel}</Label>
                    <Input
                      value={portalUserId}
                      onChange={(e) => setPortalUserId(e.target.value)}
                      placeholder={portalCfg.idPlaceholder}
                      autoComplete="off"
                      className="mt-1.5 h-10 bg-white border-neutral-300 text-neutral-950 placeholder:text-neutral-500 dark:bg-[#06241b] dark:border-emerald-400/40 dark:text-white dark:placeholder:text-white/50 focus-visible:ring-emerald-500/40"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-neutral-950 dark:text-white">Password</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showPortalPw ? "text" : "password"}
                        value={portalPassword}
                        onChange={(e) => setPortalPassword(e.target.value)}
                        placeholder={portalCfg.pwPlaceholder}
                        autoComplete="new-password"
                        className="h-10 pr-9 bg-white border-neutral-300 text-neutral-950 placeholder:text-neutral-500 dark:bg-[#06241b] dark:border-emerald-400/40 dark:text-white dark:placeholder:text-white/50 focus-visible:ring-emerald-500/40"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPortalPw((v) => !v)}
                        aria-label={showPortalPw ? "Hide password" : "Show password"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-neutral-900 dark:text-white/70 dark:hover:text-white"
                      >
                        {showPortalPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-neutral-800 dark:text-white/90">
                  <Lock className="h-4 w-4" /> সম্পূর্ণ ঐচ্ছিক — খালি রাখলেও অর্ডার সাবমিট হবে।
                </p>
              </motion.div>



              <div className="rounded-2xl p-6 space-y-4 font-sans antialiased bg-white border border-neutral-200 shadow-sm dark:bg-[#06241b] dark:border-emerald-400/40">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <h3 className="font-display font-extrabold text-xl md:text-2xl text-neutral-950 dark:text-white">
                    {isB2B ? "কাস্টমারের তথ্য" : t("uploadPage.yourDetails")}
                  </h3>
                  {isB2B && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-300 px-2.5 py-1 rounded-full dark:text-emerald-200 dark:bg-emerald-500/20 dark:border-emerald-400/40">
                      🤝 B2B — আপনার ক্লায়েন্টের তথ্য দিন
                    </span>
                  )}
                </div>
                {isB2B && (
                  <p className="text-sm font-medium text-neutral-800 dark:text-white/90 -mt-2">
                    নিচের ঘরগুলোতে আপনার ক্লায়েন্ট / এন্ড কাস্টমারের নাম, ফোন ও ইমেইল দিন। আপনার নিজের অ্যাকাউন্টের নাম এডমিন প্যানেলে B2B Agent হিসেবে রেকর্ড থাকবে।
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-sm font-semibold text-neutral-950 dark:text-white">{isB2B ? "কাস্টমারের পূর্ণ নাম" : t("uploadPage.fullName")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-white border-neutral-300 text-neutral-950 placeholder:text-neutral-500 dark:bg-[#06241b] dark:border-emerald-400/40 dark:text-white dark:placeholder:text-white/50" /></div>
                  <div><Label className="text-sm font-semibold text-neutral-950 dark:text-white">{isB2B ? "কাস্টমারের ফোন" : t("uploadPage.phone")}</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 bg-white border-neutral-300 text-neutral-950 placeholder:text-neutral-500 dark:bg-[#06241b] dark:border-emerald-400/40 dark:text-white dark:placeholder:text-white/50" /></div>
                  <div className="sm:col-span-2"><Label className="text-sm font-semibold text-neutral-950 dark:text-white">{isB2B ? "কাস্টমারের ইমেইল" : t("uploadPage.email")}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-white border-neutral-300 text-neutral-950 placeholder:text-neutral-500 dark:bg-[#06241b] dark:border-emerald-400/40 dark:text-white dark:placeholder:text-white/50" /></div>
                  <div className="sm:col-span-2"><Label className="text-sm font-semibold text-neutral-950 dark:text-white">{t("uploadPage.notes")}</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 bg-white border-neutral-300 text-neutral-950 placeholder:text-neutral-500 dark:bg-[#06241b] dark:border-emerald-400/40 dark:text-white dark:placeholder:text-white/50" rows={3} /></div>
                </div>

                {isB2B && (
                  <div className="rounded-2xl p-5 space-y-3 bg-white border border-neutral-200 shadow-sm dark:bg-[#06241b] dark:border-emerald-400/40">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <h4 className="font-display font-extrabold text-lg text-neutral-950 dark:text-white">💰 ট্যাক্স পেমেন্ট (B2B)</h4>
                      <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-300 px-2.5 py-1 rounded-full dark:text-emerald-200 dark:bg-emerald-500/20 dark:border-emerald-400/40">১% সরকারি চালান ফি অটো-যোগ</span>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-neutral-950 dark:text-white">ট্যাক্সের পরিমাণ (ঐচ্ছিক)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={b2bTaxInput}
                        onChange={(e) => setB2bTaxInput(e.target.value.replace(/[^\d.]/g, ""))}
                        placeholder="যেমন: 1000 — না থাকলে খালি রাখুন বা 0 দিন"
                        inputMode="decimal"
                        className="mt-1.5 h-11 font-mono bg-white border-neutral-300 text-neutral-950 placeholder:text-neutral-500 dark:bg-[#06241b] dark:border-emerald-400/40 dark:text-white dark:placeholder:text-white/50"
                      />
                      <p className="mt-2 text-sm font-medium text-neutral-800 dark:text-white/90">
                        খালি রাখলে বা <strong>0</strong> দিলে কোনো আপফ্রন্ট পেমেন্ট লাগবে না — অর্ডার সরাসরি সাবমিট হবে।
                        পরিমাণ &gt; 0 দিলে অর্ডার ফাইনাল করতে গেটওয়েতে পেমেন্ট করতে হবে।
                      </p>
                    </div>
                    <div className="rounded-xl bg-neutral-50 border border-neutral-200 dark:bg-[#04190f] dark:border-emerald-400/30 p-3 space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="font-semibold text-neutral-800 dark:text-white/90">সরকারি ট্যাক্স</span><span className="font-mono font-bold text-neutral-950 dark:text-white">৳ {b2bTaxAmount.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="font-semibold text-neutral-800 dark:text-white/90">সরকারি চালান ফি (১%)</span><span className="font-mono font-bold text-neutral-950 dark:text-white">৳ {b2bGovtChargeFee.toLocaleString()}</span></div>
                      <div className="h-px bg-neutral-200 dark:bg-emerald-400/30" />
                      <div className="flex justify-between text-base"><span className="font-extrabold text-neutral-950 dark:text-white">পরিশোধযোগ্য মোট</span><span className="font-mono font-extrabold text-neutral-950 dark:text-white">৳ {b2bTaxTotal.toLocaleString()}</span></div>
                    </div>
                  </div>
                )}

                <Button disabled={!canSubmit} onClick={submitOrder} className="w-full gradient-primary text-primary-foreground shadow-glow">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isB2B && b2bHasTax ? `পেমেন্টে যান — ৳${b2bTaxTotal.toLocaleString()}` : t("uploadPage.createOrder"))}
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl px-3 py-5 bg-white border border-neutral-200/80 shadow-sm dark:bg-[#06241b] dark:border-emerald-400/40 font-sans antialiased">
                <div className="flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-emerald-600 dark:text-emerald-300 shrink-0" /><h4 className="font-extrabold text-xl md:text-2xl tracking-tight text-neutral-950 dark:text-white">{t("uploadPage.dataSafe")}</h4></div>
                <ul className="mt-3 space-y-2.5 text-base md:text-lg font-semibold leading-snug text-neutral-950 dark:text-white">
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-300 shrink-0 mt-0.5" /> {t("uploadPage.safe1")}</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-300 shrink-0 mt-0.5" /> {t("uploadPage.safe2")}</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-300 shrink-0 mt-0.5" /> {t("uploadPage.safe3")}</li>
                </ul>
              </div>
              <div className="rounded-2xl px-3 py-5 bg-white border border-neutral-200/80 shadow-sm dark:bg-[#06241b] dark:border-emerald-400/40 font-sans antialiased">
                <h4 className="font-extrabold text-xl md:text-2xl tracking-tight text-neutral-950 dark:text-white">{t("uploadPage.whatToUpload")}</h4>
                <ul className="mt-3 space-y-2 text-base md:text-lg font-semibold leading-snug text-neutral-950 dark:text-white">
                  <li>• {t("uploadPage.doc1")}</li>
                  <li>• {t("uploadPage.doc2")}</li>
                  <li>• {t("uploadPage.doc3")}</li>
                  <li>• {t("uploadPage.doc4")}</li>
                  <li>• {t("uploadPage.doc5")}</li>
                </ul>
              </div>
            </div>
          </div>
          {(() => { const c = SEO_CONTENT["upload"]; return <SeoFaqBlock title={c.title} intro={c.intro} bullets={c.bullets} faqs={c.faqs} />; })()}
          <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["upload"]} />
        </div>
      </section>
    </SiteShell>
  );
}
